import React, { useMemo, useState } from 'react';
import * as provider from '../../data/community/communityDataProvider';
import {
  useCommunityDataSource,
  DATA_SOURCE_API,
} from '../../utils/communityDataSource';

const HMAC_SECRET_SET = !!process.env.REACT_APP_COMMUNITY_HMAC_SECRET;

function Field({ label, value, onChange, type = 'text', placeholder, className = '' }) {
  return (
    <div className={className}>
      <label className="text-xs text-dark-text-muted">{label}</label>
      <input
        type={type}
        className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-dark-card border border-dark-border rounded-lg p-4">
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-dark-text">{title}</h3>
        {hint ? <p className="text-xs text-dark-text-muted mt-0.5">{hint}</p> : null}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Btn({ label, onClick }) {
  return (
    <button
      type="button"
      className="px-3 py-1.5 text-xs border border-dark-border rounded hover:border-primary hover:text-primary transition-colors"
      onClick={onClick}
    >
      {label}
    </button>
  );
}

export default function CommunityPlayground() {
  const [threadId, setThreadId] = useState('1');
  const [commentId, setCommentId] = useState('1');
  const [boardId, setBoardId] = useState('1');
  const [userId, setUserId] = useState('1001');
  const [title, setTitle] = useState('Playground title');
  const [body, setBody] = useState('Playground test body');
  const [kind, setKind] = useState('general');
  const [planetId, setPlanetId] = useState('1');
  const [latitude, setLatitude] = useState('37.4979');
  const [longitude, setLongitude] = useState('127.0276');
  const [radius, setRadius] = useState('5000');
  const [tag, setTag] = useState('coffee');
  const [tags, setTags] = useState('coffee, morning');
  const [voteValue, setVoteValue] = useState('up');
  const [reportType, setReportType] = useState('1');
  const [reportDetail, setReportDetail] = useState('playground report');
  const [readDistance, setReadDistance] = useState('5000');
  const [boardName, setBoardName] = useState('playground-board');
  const [beforeId, setBeforeId] = useState('');
  const [limit, setLimit] = useState('10');
  const [page, setPage] = useState('0');
  const [lastSeenAt, setLastSeenAt] = useState(() =>
    new Date(Date.now() - 86400000).toISOString().replace(/\.\d{3}Z$/, 'Z'),
  );

  const [running, setRunning] = useState('');
  const [log, setLog] = useState('');

  const dataSource = useCommunityDataSource();
  const isApi = dataSource === DATA_SOURCE_API;

  const tid = Number(threadId);
  const cid = Number(commentId);
  const bid = Number(boardId);
  const uid = Number(userId);

  const num = (v) => (v === '' || v == null ? undefined : Number(v));
  const str = (v) => (v === '' || v == null ? undefined : v);
  const tagsArr = useMemo(
    () => tags.split(',').map((s) => s.trim()).filter(Boolean),
    [tags],
  );

  const run = async (label, fn) => {
    setRunning(label);
    const startedAt = new Date().toISOString();
    try {
      const result = await fn();
      setLog(
        `${label}  (${dataSource})  ${startedAt}\n` +
          `${JSON.stringify(result, null, 2)}`,
      );
    } catch (e) {
      setLog(
        `${label}  (${dataSource})  ${startedAt}\n` +
          `ERROR status=${e.status ?? ''} code=${e.code ?? ''}\n${e.message}` +
          (e.data ? `\n${JSON.stringify(e.data, null, 2)}` : ''),
      );
    } finally {
      setRunning('');
    }
  };

  return (
    <div className="space-y-4">
      {/* Status banner */}
      <div
        className={[
          'rounded-lg border px-4 py-3 text-xs',
          isApi
            ? 'border-sky-500/40 bg-sky-500/10 text-sky-200'
            : 'border-amber-500/40 bg-amber-500/10 text-amber-200',
        ].join(' ')}
      >
        <span className="font-semibold">
          Data source: {isApi ? 'API (admin-community-api-serverless)' : 'Mock'}
        </span>
        {isApi ? (
          <span className="ml-2">
            {HMAC_SECRET_SET
              ? 'HMAC secret is configured.'
              : 'WARNING: REACT_APP_COMMUNITY_HMAC_SECRET is not set — real calls will fail. Set it (TEST secret only) and restart.'}
          </span>
        ) : (
          <span className="ml-2">All endpoints run against the in-memory mock store.</span>
        )}
      </div>

      {/* Shared inputs */}
      <div className="bg-dark-card border border-dark-border rounded-lg p-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
        <Field label="Thread ID" value={threadId} onChange={setThreadId} />
        <Field label="Comment ID" value={commentId} onChange={setCommentId} />
        <Field label="Board ID" value={boardId} onChange={setBoardId} />
        <Field label="Acting user ID" value={userId} onChange={setUserId} />
        <Field label="Title" value={title} onChange={setTitle} />
        <div>
          <label className="text-xs text-dark-text-muted">Kind</label>
          <select
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={kind}
            onChange={(e) => setKind(e.target.value)}
          >
            <option value="general">general</option>
            <option value="regional">regional</option>
          </select>
        </div>
        <Field label="Planet ID" value={planetId} onChange={setPlanetId} />
        <Field label="Latitude" value={latitude} onChange={setLatitude} />
        <Field label="Longitude" value={longitude} onChange={setLongitude} />
        <Field label="Radius (m)" value={radius} onChange={setRadius} />
        <Field label="Tag (single)" value={tag} onChange={setTag} />
        <Field label="Tags (comma-separated)" value={tags} onChange={setTags} />
        <div>
          <label className="text-xs text-dark-text-muted">Vote value</label>
          <select
            className="w-full mt-1 bg-dark-bg border border-dark-border rounded px-3 py-2 text-sm text-dark-text"
            value={voteValue}
            onChange={(e) => setVoteValue(e.target.value)}
          >
            <option value="up">up</option>
            <option value="down">down</option>
          </select>
        </div>
        <Field label="Report type" value={reportType} onChange={setReportType} />
        <Field label="Report detail" value={reportDetail} onChange={setReportDetail} />
        <Field label="Read distance (m)" value={readDistance} onChange={setReadDistance} />
        <Field label="Board name" value={boardName} onChange={setBoardName} />
        <Field label="before_id" value={beforeId} onChange={setBeforeId} />
        <Field label="limit" value={limit} onChange={setLimit} />
        <Field label="page (trending)" value={page} onChange={setPage} />
        <Field label="last_seen_at" value={lastSeenAt} onChange={setLastSeenAt} className="sm:col-span-2" />
        <Field label="Body / patch / comment text" value={body} onChange={setBody} className="sm:col-span-3 lg:col-span-4" />
      </div>

      <Section title="Threads — CRUD" hint="POST/PATCH/DELETE/GET /api/v1/community/threads">
        <Btn label="GET thread by id" onClick={() => run('GET thread by id', () => provider.getThreadById(tid, { acting_user_id: uid }))} />
        <Btn
          label="POST thread"
          onClick={() =>
            run('POST thread', () =>
              provider.postThread(
                {
                  kind,
                  body,
                  title: str(title),
                  planet_id: num(planetId),
                  latitude: kind === 'regional' || latitude ? num(latitude) : undefined,
                  longitude: kind === 'regional' || longitude ? num(longitude) : undefined,
                  tags: tagsArr,
                },
                uid,
              ),
            )
          }
        />
        <Btn label="PATCH thread" onClick={() => run('PATCH thread', () => provider.patchThread(tid, { body: `${body} (patched)`, tags: tagsArr }, uid))} />
        <Btn label="DELETE thread" onClick={() => run('DELETE thread', () => provider.deleteThread(tid))} />
      </Section>

      <Section title="Threads — Feeds & Discovery" hint="feed, nearby, global, mine, trending, gps, by-tag, unread-count">
        <Btn label="GET feed" onClick={() => run('GET feed', () => provider.getThreadsFeed({ kind: str(kind), before_id: num(beforeId), limit: num(limit) }))} />
        <Btn label="GET nearby" onClick={() => run('GET nearby', () => provider.getThreadsNearby({ latitude: num(latitude), longitude: num(longitude), radius_m: num(radius), planet_id: num(planetId), limit: num(limit) }, uid))} />
        <Btn label="GET global" onClick={() => run('GET global', () => provider.getThreadsGlobal({ before_id: num(beforeId), limit: num(limit) }))} />
        <Btn label="GET mine" onClick={() => run('GET mine', () => provider.getMyThreads({ before_id: num(beforeId), limit: num(limit) }, uid))} />
        <Btn label="GET trending" onClick={() => run('GET trending', () => provider.getThreadsTrending({ page: num(page), limit: num(limit) }))} />
        <Btn label="GET gps" onClick={() => run('GET gps', () => provider.getThreadsGps({ before_id: num(beforeId), limit: num(limit) }))} />
        <Btn label="GET by-tag" onClick={() => run('GET by-tag', () => provider.getThreadsByTag({ tag: str(tag), before_id: num(beforeId), limit: num(limit) }))} />
        <Btn label="POST nearby unread-count" onClick={() => run('POST nearby unread-count', () => provider.postNearbyUnreadCount({ latitude: num(latitude), longitude: num(longitude), last_seen_at: lastSeenAt, planet_id: num(planetId) }, uid))} />
      </Section>

      <Section title="Comments" hint="GET/POST /threads/{id}/comments, PATCH/DELETE /comments/{id}">
        <Btn label="GET comments" onClick={() => run('GET comments', () => provider.getComments(tid, { before_id: num(beforeId), limit: num(limit) }))} />
        <Btn label="POST comment" onClick={() => run('POST comment', () => provider.postComment(tid, { body }, uid))} />
        <Btn label="PATCH comment" onClick={() => run('PATCH comment', () => provider.patchComment(cid, { body: `${body} (edited)` }, uid))} />
        <Btn label="DELETE comment" onClick={() => run('DELETE comment', () => provider.deleteComment(cid))} />
      </Section>

      <Section title="Votes" hint="POST/DELETE /threads/{id}/votes (up | down)">
        <Btn label="POST vote" onClick={() => run('POST vote', () => provider.postVote(tid, voteValue, uid))} />
        <Btn label="DELETE vote" onClick={() => run('DELETE vote', () => provider.deleteVote(tid, uid))} />
      </Section>

      <Section title="Reports" hint="POST /threads/{id}/reports (GET reports is mock-only)">
        <Btn label="POST report" onClick={() => run('POST report', () => provider.postReport(tid, { report_type: num(reportType), detail: str(reportDetail) }, uid))} />
        <Btn label="GET reports (mock-only)" onClick={() => run('GET reports', () => provider.getReports({ limit: num(limit) }))} />
      </Section>

      <Section title="Views" hint="POST /threads/{id}/views (daily dedup)">
        <Btn label="POST view" onClick={() => run('POST view', () => provider.postThreadView(tid, uid))} />
      </Section>

      <Section title="Boards" hint="POST/GET /boards, GET /boards/{id}, GET /boards/{id}/threads">
        <Btn label="POST board" onClick={() => run('POST board', () => provider.postBoard({ name: boardName, planet_id: num(planetId), latitude: num(latitude), longitude: num(longitude) }, uid))} />
        <Btn label="GET boards" onClick={() => run('GET boards', () => provider.getBoards({ latitude: num(latitude), longitude: num(longitude), radius_m: num(radius), planet_id: num(planetId), limit: num(limit) }))} />
        <Btn label="GET board by id" onClick={() => run('GET board by id', () => provider.getBoardById(bid))} />
        <Btn label="GET board threads" onClick={() => run('GET board threads', () => provider.getThreadsByBoard(bid, { before_id: num(beforeId), limit: num(limit) }))} />
      </Section>

      <Section title="Settings" hint="POST /settings/read-distance (1000-10000m)">
        <Btn label="POST read-distance" onClick={() => run('POST read-distance', () => provider.updateReadDistance({ read_distance_m: num(readDistance) }, uid))} />
      </Section>

      <Section title="Likes — DEPRECATED" hint="Superseded by votes; GET likes is mock-only">
        <Btn label="POST like" onClick={() => run('POST like', () => provider.postLike(tid, uid))} />
        <Btn label="DELETE like" onClick={() => run('DELETE like', () => provider.deleteLike(tid, uid))} />
        <Btn label="GET likes (mock-only)" onClick={() => run('GET likes', () => provider.getLikesForThread(tid))} />
      </Section>

      <Section title="Utility" hint="Mock-store maintenance">
        <Btn
          label="Reset mock store"
          onClick={() =>
            run('Reset mock store', async () => {
              provider.resetMockStore();
              return { ok: true };
            })
          }
        />
      </Section>

      <div>
        <div className="flex items-center justify-between">
          <label className="text-xs text-dark-text-muted">Response</label>
          {running ? <span className="text-xs text-primary">Running: {running}…</span> : null}
        </div>
        <pre className="mt-2 p-3 bg-dark-bg border border-dark-border rounded text-xs overflow-auto max-h-96 text-dark-text-secondary whitespace-pre-wrap">
          {log || '—'}
        </pre>
      </div>
    </div>
  );
}
